/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, Crown, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DoctorCard } from './DoctorCard';

export const URGENCY_BADGE_CLS = {
  routine: 'badge-online',
  urgent: 'badge-waiting',
  emergency: 'badge-emergency',
};

export const SKELETON_KEYS = ['sk1', 'sk2', 'sk3', 'sk4', 'sk5', 'sk6'];

/** Doctors grid body: loading → skeletons, empty → message, else cards. */
export const DoctorsGridBody = ({ loading, filtered, limits, onBook, emptyLabel }) => {
  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {SKELETON_KEYS.map((k) => <Skeleton key={k} className="h-52 rounded-2xl" />)}
      </div>
    );
  }
  if (filtered.length === 0) {
    return (
      <Card className="p-8 text-center" data-testid="no-doctors-empty">
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      </Card>
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {filtered.map((d, idx) => (
        <DoctorCard key={d.id} doctor={d} limits={limits} onBook={onBook} priorityAccent={idx === 0} />
      ))}
    </div>
  );
};

export const PremiumBanner = ({ limits, config, onUpgrade, loading }) => {
  const { t } = useTranslation();
  if (!limits || !config) return null;
  const active = limits.premium_active;
  const expiry = limits.premium_expiry_date ? new Date(limits.premium_expiry_date).toLocaleDateString('it-IT') : null;

  if (active) {
    return (
      <div className="rounded-2xl bg-gradient-to-r from-accent/15 via-accent/8 to-transparent border border-accent/30 p-4 flex items-center gap-3" data-testid="premium-active-banner">
        <div className="h-10 w-10 rounded-2xl bg-accent/20 text-accent flex items-center justify-center">
          <Crown className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-semibold">{t('premiumActiveTitle')}</p>
            <Badge variant="success" data-testid="premium-active-badge"><CheckCircle2 className="h-3 w-3 mr-1" />{t('premiumActive')}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{t('premiumActiveDesc', { date: expiry })}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 md:p-5 flex items-start gap-4" data-testid="patient-premium-card">
      <div className="h-11 w-11 rounded-2xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
        <Crown className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display font-semibold text-lg">{t('premiumTitle')}</p>
        <p className="text-sm text-muted-foreground mt-0.5">{t('premiumPitch', { price: config.patient_premium_monthly_eur.toFixed(2) })}</p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-primary" /> {t('premiumBenefit1', { count: config.premium_free_consultations })}</span>
          <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-primary" /> {t('premiumBenefit2', { pct: config.premium_discount_pct })}</span>
          <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-primary" /> {t('premiumBenefit4')}</span>
        </div>
      </div>
      <Button onClick={onUpgrade} disabled={loading} size="sm" data-testid="patient-premium-upgrade-btn" className="flex-shrink-0">
        {loading ? t('processing') : t('premiumCTA', { price: config.patient_premium_monthly_eur.toFixed(2) })}
      </Button>
    </div>
  );
};

export const LimitsPill = ({ limits }) => {
  const { t } = useTranslation();
  if (!limits) return null;
  const out = limits.remaining === 0;
  return (
    <div className={cn('inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold', out ? 'badge-waiting' : 'badge-online')} data-testid="limits-pill">
      <Sparkles className="h-3 w-3" />
      {out ? t('telemedFreeExhausted') : t('telemedFreeRemaining', { count: limits.remaining })}
    </div>
  );
};

